import type { GetBrandDTO } from "@nlc/shared-types";

import { ShareIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { TabList, Tabs } from "react-aria-components";

import { Button } from "@/components/button";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/icons";
import { Tab, TabPanel } from "@/components/tabs";

import { AssetsTab } from "./tabs/AssetsTab";
import { ColorsFontsTab } from "./tabs/ColorsFontsTab";
import { KnowledgeTab } from "./tabs/KnowledgeTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { RequestsTab } from "./tabs/RequestsTab";
import { VisualDirectionTab } from "./tabs/VisualDirectionTab";

const TABS = [
  { component: OverviewTab, id: "overview", label: "Overview" },
  { component: AssetsTab, id: "assets", label: "Assets" },
  { component: ColorsFontsTab, id: "colors-fonts", label: "Colors/fonts" },
  { component: RequestsTab, id: "requests", label: "Requests" },
  { component: KnowledgeTab, id: "knowledge", label: "Knowledge" },
  {
    component: VisualDirectionTab,
    id: "visual-direction",
    label: "Visual direction",
  },
];

const SCROLL_STEP = 160;

type BrandDetailsTabsProps = {
  brand: GetBrandDTO;
};

export function BrandDetailsTabs({ brand }: BrandDetailsTabsProps) {
  const { canScrollLeft, canScrollRight, scrollBy, scrollRef } =
    useHorizontalScroll();

  return (
    <div className="flex flex-col gap-lg">
      <Tabs className="flex flex-col">
        <div className="flex items-center gap-md">
          <ScrollButton
            direction="left"
            isDisabled={!canScrollLeft}
            onPress={() => scrollBy(-SCROLL_STEP)}
          />
          <div
            className="min-w-0 flex-1 overflow-x-auto scrollbar-none"
            ref={scrollRef}
          >
            <TabList aria-label="Brand details tabs" className="flex gap-lg">
              {TABS.map((tab) => (
                <Tab
                  className="shrink-0 whitespace-nowrap text-base"
                  id={tab.id}
                  key={tab.id}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </div>
          <ScrollButton
            direction="right"
            isDisabled={!canScrollRight}
            onPress={() => scrollBy(SCROLL_STEP)}
          />
        </div>

        {TABS.map(({ component: Component, id }) => (
          <TabPanel className="max-h-[45vh] overflow-y-auto" id={id} key={id}>
            <Component brand={brand} />
          </TabPanel>
        ))}
      </Tabs>

      <Button
        asChild
        className="mt-xs w-full"
        color="color"
        size="md"
        variant="primary"
      >
        <a href={`/brands/${brand.id}/edit`} rel="noreferrer" target="_blank">
          Brand details
          <ShareIcon />
        </a>
      </Button>
    </div>
  );
}

function ScrollButton({
  direction,
  isDisabled,
  onPress,
}: {
  direction: "left" | "right";
  isDisabled: boolean;
  onPress: () => void;
}) {
  const Icon = direction === "left" ? ChevronLeftIcon : ChevronRightIcon;

  return (
    <button
      aria-label={`Scroll tabs ${direction}`}
      className="shrink-0 cursor-pointer text-fg-quinary transition-opacity disabled:cursor-default disabled:opacity-30"
      disabled={isDisabled}
      onClick={onPress}
      type="button"
    >
      <Icon className="size-6" />
    </button>
  );
}

function useHorizontalScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;

    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);

    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [update]);

  const scrollBy = useCallback((left: number) => {
    scrollRef.current?.scrollBy({ behavior: "smooth", left });
  }, []);

  return { canScrollLeft, canScrollRight, scrollBy, scrollRef };
}
